import React, { useMemo, useRef } from 'react';
import { styled, useStyletron } from 'baseui';
import type { AnnotationInfoT } from '../annotations/types';
import { AnnotationContent } from './annotation-content';
import type { ComponentRendererT } from './types';
type PropsT = {
  annotation: AnnotationInfoT;
  icon: React.ReactNode;
  isFocused: boolean;
  selectAnnotationCheckbox?: ComponentRendererT;
  objectInstantiationButton?: ComponentRendererT;
  editButton?: ComponentRendererT;
  showHideButton?: ComponentRendererT;
  deleteButton?: ComponentRendererT;
  moreActionsButton?: ComponentRendererT;
  subContent?: ComponentRendererT;
};
const Container = styled<'div', {
  $borderColor: string;
  $disabled: boolean;
}>('div', ({
  $theme,
  $borderColor,
  $disabled
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale500,
  padding: `${$theme.sizing.scale500} ${$theme.sizing.scale300}`,
  borderRadius: $theme.borders.radius300,
  border: `2px solid ${$borderColor}`,
  marginTop: $theme.sizing.scale300,
  transition: 'border 0.1s ease-in-out',
  opacity: $disabled ? 0.5 : 1
}));
const ContentContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale500,
  flex: 1
}));
const ContentRow = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: $theme.sizing.scale300
}));
const ActionButtonsRow = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale200
}));
export const AnnotationDetails = ({
  annotation,
  icon,
  isFocused,
  selectAnnotationCheckbox,
  objectInstantiationButton,
  editButton,
  showHideButton,
  deleteButton,
  moreActionsButton,
  subContent
}: PropsT) => {
  const [, theme] = useStyletron();
  const {
    isSelected,
    isDisabled
  } = annotation;
  const ref = useRef<HTMLDivElement>(null);
  const borderColor = useMemo(() => {
    if (isSelected) {
      // eslint-disable-next-line react-hooks/refs
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return theme.colors.borderAccentLight;
    }
    if (isFocused) {
      return theme.colors.borderOpaque;
    }
    return 'transparent';
  }, [isFocused, isSelected, theme.colors.borderOpaque, theme.colors.borderAccentLight]);
  return <Container ref={ref} $borderColor={borderColor} $disabled={isDisabled}>
      {selectAnnotationCheckbox && selectAnnotationCheckbox(annotation)}
      <ContentContainer>
        <ContentRow>
          <AnnotationContent annotation={annotation} icon={icon} />
          <ActionButtonsRow onClick={e => e.stopPropagation()}>
            {objectInstantiationButton && objectInstantiationButton(annotation)}
            {editButton && editButton(annotation)}
            {showHideButton && showHideButton(annotation)}
            {deleteButton && deleteButton(annotation)}
            {moreActionsButton && moreActionsButton(annotation)}
          </ActionButtonsRow>
        </ContentRow>
        {subContent && subContent(annotation)}
      </ContentContainer>
    </Container>;
};